import React, { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { useForm } from "react-hook-form";
import Table from "@enerex/unified-ui-react/Table";
import FormInput from "@enerex/unified-ui-react/form/FormInput";
import LoadingButton from "@enerex/unified-ui-react/LoadingButton";
import FilterPills from "components/Table/FilterPills";
import { trimInvalidDate } from "helpers/api/utils";
import { getNotificationMessage } from "../utils";

type NotificationFilters = { from: string; to: string };
const emptyFilters: NotificationFilters = { from: "", to: "" };

export const NotificationFilterForm = ({ globalFilter, setGlobalFilter, hide }) => {
    const { control, handleSubmit, reset } = useForm<{ from: Date | null; to: Date | null }>({
        defaultValues: {
            from: globalFilter?.from ? parseISO(globalFilter.from) : null,
            to: globalFilter?.to ? parseISO(globalFilter.to) : null,
        },
    });
    const apply = (values: { from: Date | null; to: Date | null }) => {
        setGlobalFilter({
            from: values.from ? format(values.from, "yyyy-MM-dd") : "",
            to: values.to ? format(values.to, "yyyy-MM-dd") : "",
        });
        hide();
    };
    return (
        <form onSubmit={handleSubmit(apply)}>
            <FormInput type="date" name="from" id="notification-date-from"
                label="Date From" htmlFor="notification-date-from"
                control={control} containerClass="mb-3" />
            <FormInput type="date" name="to" id="notification-date-to"
                label="Date To" htmlFor="notification-date-to"
                control={control} containerClass="mb-3" />
            <div className="d-flex justify-content-end gap-2">
                <LoadingButton type="submit">Apply</LoadingButton>
                <LoadingButton type="button" variant="outline-secondary"
                    onClick={() => { reset({ from: null, to: null }); apply({ from: null, to: null }); }}>Reset</LoadingButton>
            </div>
        </form>
    );
};

const NotificationAppliedFilters = ({ globalFilters, setGlobalFilters }) => (
    <FilterPills filters={[
        { label: "Date From", value: globalFilters?.from,
            onRemove: () => setGlobalFilters({ ...globalFilters, from: "" }) },
        { label: "Date To", value: globalFilters?.to,
            onRemove: () => setGlobalFilters({ ...globalFilters, to: "" }) },
    ]} />
);

export const NotificationsData = ({ total, hook, notifications = [], pageSizes = [], loading, noDataMessage }) => {
    const { limit, setLimit, page, setPage, orderBy, setOrderBy,
        ascendingOrder, setAscendingOrder, filters, setFilters } = hook();
    const pagination = { pageIndex: page, pageSize: limit };
    const sorting = orderBy ? [{ id: String(orderBy), desc: !ascendingOrder }] : [];
    const columns = useMemo(() => [
        { header: "Date", accessorKey: "CreatedOn",
            cell: ({ row }) => trimInvalidDate(row.original.createdOn?.split("T")[0]),
            enableSorting: true },
        { header: "Description", accessorKey: "message",
            cell: ({ row }) => getNotificationMessage(row.original), enableSorting: false },
    ], []);

    return (
        <Table columns={columns} data={notifications || []} total={total} loading={loading}
            noDataMessage={noDataMessage} enableSearch={false} enableFilters
            enableHideColumns enableSorting enablePagination enableSelection={false}
            enablePaginateAll={false} enableUrlPagination={false}
            enableColumnVisibilityStorage={false} enableGlobalFilterStorage={false}
            pageSizes={pageSizes.length ? pageSizes : [50, 100, 200]}
            pagination={pagination} onPaginationChange={updater => {
                const next = typeof updater === "function" ? updater(pagination) : updater;
                setLimit(next.pageSize);
                setPage(next.pageSize !== limit ? 0 : next.pageIndex);
            }}
            sorting={sorting} onSortingChange={updater => {
                const next = typeof updater === "function" ? updater(sorting) : updater;
                setOrderBy(next[0]?.id);
                setAscendingOrder(next[0] ? !next[0].desc : false);
                setPage(0);
            }}
            globalFilter={filters} onGlobalFilterChange={updater => {
                const next = typeof updater === "function" ? updater(filters) : updater;
                setFilters({ ...emptyFilters, ...next });
                setPage(0);
            }}
            filterComponent={NotificationFilterForm}
            appliedFilters={NotificationAppliedFilters}
        />
    );
};
